// Scroll-related configuration constants

// Navbar scroll threshold - navbar style changes after scrolling this many pixels
export const NAVBAR_SCROLL_THRESHOLD = 40;

// Mobile breakpoint in pixels
export const MOBILE_BREAKPOINT = 768;

// Buffer space below navbar
export const SCROLL_BUFFER = 10;

// Per-section extra offset added below the navbar. Used to hide the
// bottom border of the preceding section on sections where it looks off.
const SECTION_EXTRA_OFFSET = {
  experience: 20,
};

/**
 * Get the current navbar height by measuring the DOM
 */
export const getNavbarHeight = () => {
  const navbar = document.querySelector('nav.navbar');
  return navbar ? navbar.offsetHeight : 70;
};

// Store the collapsed navbar height for scroll calculations
let collapsedNavbarHeight = 70;

/**
 * Measure the collapsed navbar height.
 *
 * On mobile, measures the visible top row (brand + toggle) plus the navbar's
 * vertical padding rather than navbar.offsetHeight. Bootstrap's collapsible
 * menu element contributes height during the `.collapsing` transition state,
 * so offsetHeight returns mid-animation values. The top-row approach is
 * stable whether the menu is open, closed, or mid-transition.
 */
const measureCollapsedHeight = () => {
  const navbar = document.querySelector('nav.navbar');
  if (!navbar) return collapsedNavbarHeight;

  // On desktop (Bootstrap lg breakpoint is 992px), navbar has no collapsible menu
  if (window.innerWidth >= 992) {
    return navbar.offsetHeight;
  }

  const container = navbar.querySelector(':scope > .container, :scope > .container-fluid');
  if (container) {
    const styles = getComputedStyle(navbar);
    const padTop = parseFloat(styles.paddingTop) || 0;
    const padBottom = parseFloat(styles.paddingBottom) || 0;
    return container.offsetHeight + padTop + padBottom;
  }

  // Fallback: trust offsetHeight only when menu is fully collapsed
  const navbarCollapse = navbar.querySelector('.navbar-collapse');
  const isExpanded = navbarCollapse && navbarCollapse.classList.contains('show');
  if (!isExpanded) return navbar.offsetHeight;
  return collapsedNavbarHeight;
};

/**
 * Update the CSS variable and stored height
 */
const updateNavbarHeightVariable = () => {
  collapsedNavbarHeight = measureCollapsedHeight();
  document.documentElement.style.setProperty('--navbar-height', `${collapsedNavbarHeight}px`);
};

/**
 * Initialize navbar height and set up listeners
 */
export const initializeNavbarHeight = () => {
  updateNavbarHeightVariable();

  // Defer non-critical listener/observer setup until the browser is idle so
  // hydration can finish paint first. iOS Safari blocks touch input while the
  // main thread is busy; installing these eagerly on mount extended the
  // "can't scroll for the first second" window.
  const deferredSetup = () => {
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateNavbarHeightVariable, 100);
    });

    const navbarCollapse = document.querySelector('.navbar-collapse');
    if (navbarCollapse) {
      const observer = new MutationObserver(() => {
        if (!navbarCollapse.classList.contains('show')) {
          setTimeout(updateNavbarHeightVariable, 50);
        }
      });
      observer.observe(navbarCollapse, { attributes: true, attributeFilter: ['class'] });
    }
  };

  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(deferredSetup, { timeout: 1000 });
  } else {
    setTimeout(deferredSetup, 200);
  }

  return collapsedNavbarHeight;
};

/**
 * Get the stored collapsed navbar height
 */
export const getCollapsedNavbarHeight = () => collapsedNavbarHeight;

// Active tween state so a new call can cancel a previous in-flight tween
// cleanly (no zombie RAF loops, no leaked listeners).
let activeTween = null;

const INTERRUPT_EVENTS = ['touchstart', 'pointerdown', 'wheel'];
const MAX_TWEEN_MS = 1200;

const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Cancel any in-flight tween: stops its RAF loop and removes its listeners.
 */
const cancelActiveTween = () => {
  if (!activeTween) return;
  if (activeTween.rafId) cancelAnimationFrame(activeTween.rafId);
  INTERRUPT_EVENTS.forEach((evt) =>
    window.removeEventListener(evt, activeTween.onInteract, { capture: true })
  );
  activeTween = null;
};

/**
 * Continuous-follow RAF tween. Each frame `getTargetY()` is re-evaluated so
 * the tween tracks layout transitions (.shifted removal, navbar-collapse,
 * .scrolled thickening) as they animate — producing one continuous motion
 * instead of an initial-scroll-then-correction two-step.
 *
 * Cancels on any of touchstart/pointerdown/wheel — the same three signals
 * proven to protect iOS Safari touch momentum (see commit 4a3f256).
 */
const tweenScroll = (getTargetY) => {
  cancelActiveTween();

  if (prefersReducedMotion()) {
    const target = getTargetY();
    if (target == null) return;
    window.scrollTo({ top: target, behavior: 'auto' });
    return;
  }

  const startY = window.scrollY;
  const initialTarget = getTargetY();
  if (initialTarget == null) return;

  const initialDistance = Math.abs(initialTarget - startY);
  if (initialDistance < 1) return;

  const duration = Math.min(600, Math.max(300, initialDistance * 0.4));

  const tween = { rafId: null, onInteract: null, startTime: null };
  tween.onInteract = () => cancelActiveTween();
  INTERRUPT_EVENTS.forEach((evt) =>
    window.addEventListener(evt, tween.onInteract, { passive: true, capture: true })
  );
  activeTween = tween;

  const step = (now) => {
    if (activeTween !== tween) return;

    // Capture startTime on the first frame that actually paints, not before
    // scheduling. If the browser delayed the first vsync (hydration, React
    // re-render), starting the clock here avoids a teleport jump on frame 1.
    if (tween.startTime === null) tween.startTime = now;
    const elapsed = now - tween.startTime;

    const target = getTargetY();
    if (target == null) {
      cancelActiveTween();
      return;
    }

    const t = Math.min(1, elapsed / duration);
    const eased = easeOutQuart(t);
    const next = startY + (target - startY) * eased;
    window.scrollTo(0, next);

    const settled = t >= 1 && Math.abs(window.scrollY - target) < 0.5;
    if (settled || elapsed >= MAX_TWEEN_MS) {
      cancelActiveTween();
      return;
    }

    tween.rafId = requestAnimationFrame(step);
  };

  tween.rafId = requestAnimationFrame(step);
};

/**
 * Smooth scroll to a fixed Y position.
 * @param {number} targetPosition - The Y position to scroll to
 */
export const smoothScroll = (targetPosition) => {
  tweenScroll(() => targetPosition);
};

/**
 * Scrolls to a section by ID.
 *
 * Uses a continuous-follow RAF tween that recomputes the target Y every frame
 * from live layout (`getBoundingClientRect`, `getNavbarHeight`). This absorbs
 * concurrent `.shifted` removal, Bootstrap navbar-collapse, and `.scrolled`
 * thickening into a single smooth motion — no separate post-settle correction.
 *
 * @param {string} sectionId - The ID of the section to scroll to
 */
export const scrollToSection = (sectionId) => {
  const extraOffset = SECTION_EXTRA_OFFSET[sectionId] || 0;

  const getTargetY = () => {
    const el = document.getElementById(sectionId);
    if (!el) return null;
    const absoluteTop = el.getBoundingClientRect().top + window.scrollY;
    return absoluteTop - getNavbarHeight() - SCROLL_BUFFER + extraOffset;
  };

  tweenScroll(getTargetY);
};
